import {ScenarioTemplatePlan, TemplateBlock, ArticleListItem, Article} from '../model/model';

function _escape(urlValue) {
  return encodeURIComponent(urlValue);
}

function resultSanityCheck(result) {
  if (!result) {
    throw new Error('Empty result');
  }
}

/**
* @param key string
* @param values array
**/
function _toUrlArray(key, values) {
  return values.map(value => `${key}[]=`+_escape(value)).join('&');
}

export class appServices {
  constructor($http, $q, $sce, $log, APIConfig, appModel) {
    "ngInject";
    this.$http = $http;
    this.$q = $q;
    this.$sce = $sce;
    this.$log = $log;
    this.APIConfig = APIConfig;
    this.appModel = appModel;
  }

  login() {
    const uoid = _escape(this.appModel.uoid);
    const tokenParameters = this.appModel.token ?
      `&token=${_escape(this.appModel.token)}&email=${this.appModel.username}` : '';
    
    const Turl = `${this.APIConfig.baseUrl}/login?uoid=${uoid}${tokenParameters}`;
    // const Turl = this.$sce.trustAsResourceUrl(url);
    return this.$http.get(Turl)
      .then(result => {
          resultSanityCheck(result);
          this.appModel.setLoginInfo(result.data);
      })
      .catch(error => {
        /* Error level = warning, because we may get the login info from cache. */
        this.appModel.setError('app.services:login()', this.APIConfig.error.LOGIN_ERROR, this.APIConfig.errorLevel.WARNING);
        /* Try to get login info from local storage. Perhaps only fallback should be retrieved from local storage. */
        this.appModel.setLoginInfo();
        return this.$q.reject(error);
      });
  }

  getSystemInfo() {
    const uoid = _escape(this.appModel.uoid);
    if (this.appModel.uoid) {
    const tokenParameters = this.appModel.token ?
      `&token=${_escape(this.appModel.token)}&email=${this.appModel.username}` : '';
      const url = `${this.APIConfig.baseUrl}/systeminfo?uoid=${uoid}${tokenParameters}`;
      return this.$http.get(url)
        .then(result => {
          resultSanityCheck(result)
          this.appModel.setSystemInfo(result.data);
        })
        .catch(error => {
          this.appModel.setError('app.services:getSystemInfo()', this.APIConfig.error.SYSTEM_INFO_ERROR, this.APIConfig.errorLevel.BLOCKING);
          return this.$q.reject(error);
        });
    } else {
      return this.$q.reject(this.APIConfig.error.NO_CREDENTIALS);
    }
  }

  /**
  *
  * @param  moment $from  Start date
  * @param  moment $to  End date
  **/
  getScenarioTemplatePlan(from, to) {
    const fromString = from.format();
    const toString = from.format();
    const tokenParameters = this.appModel.token ?
      `&token=${_escape(this.appModel.token)}&email=${this.appModel.username}` : '';
    const uri = 'uoid=' + _escape(this.appModel.uoid) + '&from=' + _escape(fromString) + '&to=' + _escape(toString);
    return this.$http.get(`${this.APIConfig.baseUrl}/scenariotemplateplan?${uri}${tokenParameters}`)
      .then(result => {
        resultSanityCheck(result);
        /* Translate string dates to moment objects */
        result.scenarioTemplatePlan = result.scenarioTemplatePlan.map(vo => new ScenarioTemplatePlan(vo));
        return result;
      })
      .catch(error => {
        this.appModel.setError('app.services:getScenarioTemplatePlan()', this.APIConfig.error.SCENARIO_TEMPLATE_PLAN_ERROR, this.APIConfig.errorLevel.BLOCKING);
        return this.$q.reject(error);
      });
  }

  /**
  * Get articles based on scenario blocks and a time interval. Articles are
  * fetched in two steps:
  * - get article list
  * - get articles that have not already been fetched
  * - combine newly fetched articles with previously fetched articles.
  **/
  getArticles(from, to, scenarioBlockIds) {
    const deferred = this.$q.defer();
    const fromString = from.format();
    const toString = to.format();
    const blockIds = _toUrlArray('id', scenarioBlockIds);
    const tokenParameters = this.appModel.token ?
      `&token=${_escape(this.appModel.token)}&email=${this.appModel.username}` : '';

    /* Get article list */
    const uri = 'uoid=' + _escape(this.appModel.uoid) + '&from=' + _escape(fromString) + '&to=' + _escape(toString) + 'scenarioblocks=' + blockIds;
    this.$http.get(`${this.APIConfig.baseUrl}/articlelist?${uri}${tokenParameters}`)
      .then(articleListItems => {
        resultSanityCheck(articleListItems);
        try {
          articleListItems = articleListItems.map(item => new ArticleListItem(item));
          const allIds = articleListItems.map(item => item.id);

          /* Remove all articles from cache that are not in the current list.
             This can perhaps be done in a seperate poll with a larger validity interval (e.g. a day).
          */
          this.appModel.removeUnreferencedArticles(allIds);

          const unfetchedArticleIds = this.appModel.getUnfetchedArticleIds(articleListItems);
          const fetchedArticleIds = _.difference(allIds, unfetchedArticleIds);
          if (unfetchedArticleIds.length > 0) {
            /*  Get articles that have not already been fetched */
            const ids = _toUrlArray('id', unfetchedArticleIds);
            this.$http.get(`${this.APIConfig.baseUrl}/article?uoid=${_escape(this.appModel.uoid)}&${ids}${tokenParameters}`)
              .then(result => {
                const articles = result.map(article => new Article(article));

                this.appModel.addArticles(articles);
                /* Combine newly fetched articles with previously fetched articles. */
                const fetchedArticles = this.appModel.getArticles(fetchedArticleIds);

                deferred.resolve(articles.concat(fetchedArticles));
              })
              .catch(error => {
                this.appModel.setError('app.services:getArticles()', this.APIConfig.error.ARTICLE_ERROR, this.APIConfig.errorLevel.BLOCKING);
              });
          } else {
            const fetchedArticles = this.appModel.getArticles(fetchedArticleIds);
            deferred.resolve(fetchedArticles);
          }
        } catch (error) {
          this.appModel.setError('app.services:getArticles()', this.APIConfig.error.ARTICLE_ERROR, this.APIConfig.errorLevel.BLOCKING);
          deferred.reject(reason);
        }
      })
      .catch(error => {
        this.appModel.setError('app.services:getArticles()', this.APIConfig.error.ARTICLE_LIST_ERROR, this.APIConfig.errorLevel.BLOCKING);
        deferred.reject(error);
      });
    return deferred.promise;
  }

  getTemplates(ids) {
    ids = Array.isArray(ids) ? ids : [ids];
    const idString = _toUrlArray('id', ids);
    const uri = 'uoid=' + _escape(this.appModel.uoid) + '&' + idString;
    const tokenParameters = this.appModel.token ?
      `&token=${_escape(this.appModel.token)}&email=${this.appModel.username}` : '';

    return this.$http.get(`${this.APIConfig.baseUrl}/template?${uri}${tokenParameters}`)
      .then(result => {
        resultSanityCheck(result);
        try {
          const templates = result;
          templates.forEach(template => {
            if (template.stylesheet) {
              this.appModel.addStylesheetReference(template.stylesheet);
            }
            template.templateBlocks = template.templateBlocks.map(block => new TemplateBlock(block));
          });
          return templates;
        } catch (error) {
          this.appModel.setError('app.services:getTemplates()', this.APIConfig.error.TEMPLATE_ERROR, this.APIConfig.errorLevel.BLOCKING);
          const reason = this.APIConfig.error.SERVICE_PROCESSING_ERROR;
          return this.$q.reject(reason);
        }
      })
      .catch(error => {
        this.appModel.setError('app.services:getTemplates()', this.APIConfig.error.TEMPLATE_ERROR, this.APIConfig.errorLevel.BLOCKING);
        return this.$q.reject(error);
      });
  }
}



// WEBPACK FOOTER //
// ./client/app/services/app.services.js